/**
 * Map initialization functions for Leaflet maps.
 *
 * Migrated from Sprockets (app/assets/javascripts/map.js) to esbuild.
 * Leaflet and plugins are imported via node_modules (CommonJS/ESM).
 *
 * IMPORTANT: All Leaflet plugins use require() instead of import because
 * ES import statements are hoisted — they execute before any other code.
 * Plugins need window.L to be set BEFORE they load (they attach to the
 * global L object). require() is sequential and respects execution order.
 */
import L from 'leaflet';

// Expose L globally BEFORE loading plugins — they attach to window.L
window.L = L;

// Leaflet plugins (sequential require — they need window.L)
//
// IMPORTANT: mapbox.js MUST load FIRST because it replaces window.L with
// its own Leaflet instance (module.exports = window.L = require('leaflet')).
// Loading it after plugins would wipe out their attachments.
try { require('mapbox.js'); } catch (e) { console.warn('[map_init] mapbox.js:', e.message); }

// Now load plugins that attach to window.L — they'll attach to the FINAL L
const mcExports = require('leaflet.markercluster/dist/leaflet.markercluster-src.js');
// Manually re-attach if UMD exported to CommonJS exports rather than the global L
if (!L.MarkerClusterGroup && mcExports) {
    L.MarkerClusterGroup = mcExports.MarkerClusterGroup || mcExports.default;
    if (L.MarkerClusterGroup) {
        L.markerClusterGroup = function (options) { return new L.MarkerClusterGroup(options); };
    }
}
try { require('leaflet-hash'); } catch (e) { console.warn('[map_init] leaflet-hash:', e.message); }
try { require('leaflet.fullscreen'); } catch (e) { console.warn('[map_init] leaflet.fullscreen:', e.message); }
try { require('leaflet.locatecontrol'); } catch (e) { console.warn('[map_init] leaflet.locatecontrol:', e.message); }
try { require('leaflet-plugins/layer/tile/Bing'); } catch (e) { console.warn('[map_init] Bing:', e.message); }
try { require('leaflet-google-places-autocomplete/src/js/leaflet-gplaces-autocomplete'); } catch (e) { console.warn('[map_init] gplaces:', e.message); }
try {
    const Spinner = require('spin.js');
    const spinFactory = require('leaflet-spin');
    // leaflet-spin in CommonJS mode exports a factory (L, Spinner) => L
    if (typeof spinFactory === 'function') { spinFactory(L, Spinner); }
} catch (e) { console.warn('[map_init] leaflet-spin:', e.message); }

function _addLayersToMap(map) {
    var ggl_sat = new L.tileLayer('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
        maxZoom: 19,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    });
    var ggl_hyb = new L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
        maxZoom: 19,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    });
    var ggl_road = new L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
        maxZoom: 19,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    });
    map.scrollWheelZoom.disable();

    var osm = new L.tileLayer('//a.tile.openstreetmap.org/{z}/{x}/{y}.png');

    var layers = {
        'Google (Road)': ggl_road,
        'Google (Sat)': ggl_sat,
        'Google (Hyb)': ggl_hyb,
        'OSM': osm
    };

    // Add Bing layer if available
    if (L.BingLayer) {
        var bing = new L.BingLayer("AjPs_cxhvGXrP52k9XHY0wKbw0yfp1XV97JD89B1JLwsndt_7JUIhtH_kVa52mIk", { type: "Aerial" });
        layers['Bing'] = bing;
    }

    // Add Mapbox layers if available
    if (L.mapbox) {
        L.mapbox.accessToken = 'pk.eyJ1Ijoia2hlbGFsIiwiYSI6IjJUUkFjYTgifQ.RRczLTrCVpftSWDPiUGHTA';
        var mapbox_road = L.mapbox.styleLayer('mapbox://styles/mapbox/streets-v9');
        var mapbox_light = L.mapbox.styleLayer('mapbox://styles/mapbox/light-v9');
        var mapbox_sat = L.mapbox.styleLayer('mapbox://styles/mapbox/satellite-streets-v9');
        map.addLayer(mapbox_light);
        layers['MB (light)'] = mapbox_light;
        layers['MB (road)'] = mapbox_road;
        layers['MB (sat)'] = mapbox_sat;
    } else {
        // Fallback: use OSM as default layer
        map.addLayer(osm);
    }

    map.addControl(new L.Control.Layers(layers, {}));
}

window.singleLeafLetMapInit = function (selector, lat, lng) {
    if (lat == null || lng == null) { return false; }
    var el = typeof jQuery !== 'undefined' ? jQuery(selector).get(0) : document.querySelector(selector);
    if (!el) { return false; }
    var map = L.map(el).setView([lat, lng], 13);
    _addLayersToMap(map);
    L.marker([lat, lng]).addTo(map);
};

window.singleAdminLeafLetMapInit = function (selector, lat, lng) {
    return window.singleLeafLetMapInit(selector, lat, lng);
};

window.leafLetMapInit = function () {
    var initlatitude = 39.91;
    var initlongitude = 116.38;
    var initZoom = 2;

    L.Icon.Default.imagePath = '//cdn.jsdelivr.net/leaflet/1.3.1/images/';

    var mapOptions = {
        scrollWheelZoom: false,
        keyboard: false
    };

    // Add fullscreen if available
    if (L.Control.Fullscreen) {
        mapOptions.fullscreenControl = true;
        mapOptions.fullscreenControlOptions = { position: 'topleft' };
    }

    var map = L.map('map', mapOptions).setView([initlatitude, initlongitude], initZoom);
    window.map = map;

    _addLayersToMap(map);

    // Google Places autocomplete if available
    if (L.Control.GPlaceAutocomplete) {
        map.addControl(new L.Control.GPlaceAutocomplete({
            callback: function (place) {
                var loc = place.geometry.location;
                var vp = place.geometry.viewport;
                map.panTo([loc.lat(), loc.lng()]);
                map.fitBounds([
                    [vp.getSouthWest().lat(), vp.getSouthWest().lng()],
                    [vp.getNorthEast().lat(), vp.getNorthEast().lng()]
                ]);
            }
        }));
    }

    // Find my Location
    if (L.control.locate) {
        map.addControl(new L.control.locate({ position: 'topright' }));
    }

    // Create Marker Cluster
    window.markers = L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        maxClusterRadius: 60,
        removeOutsideVisibleBounds: true,
        disableClusteringAtZoom: 18,
        animate: false
    });

    map.on('zoomend', function () {
        map.closePopup();
    });

    window.markers.on('clusterclick', function () {
        map.closePopup();
    });

    // Keep browser history via leaflet-hash
    if (typeof map.addHash === 'function') {
        map.addHash();
    }
};
